/**
 * CR282: User-Proof Eligibility Date Range Formatting
 * 
 * Prevents ambiguous date compression (e.g., "Jan 28–3") by always showing
 * explicit month and day on both ends of the range.
 * 
 * Examples:
 * - 2024-01-29 → 2024-02-04  ⇒ "Jan 29 – Feb 4, 2024"
 * - 2026-01-08 → 2026-01-14  ⇒ "Jan 8 – Jan 14, 2026" (NOT "Jan 8–14")
 * - 2024-12-28 → 2025-01-03  ⇒ "Dec 28, 2024 – Jan 3, 2025"
 * - Only start date            ⇒ "From Jan 29, 2024"
 * - Only end date              ⇒ "Until Feb 4, 2024"
 * 
 * USAGE:
 * - Published cards: use v_public_active_deals.valid_from / valid_until
 * - Staging/preview: use cr075_staging_deal_items.validity_start_date / validity_end_date
 * - Fallback: plain text (validity_raw_text) but do not attempt to "range format" it
 */

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Format an eligibility date range with explicit month/day on both ends.
 * Returns null if both dates are missing.
 */
export function formatEligibilityRange(
  validFrom?: string | Date | null,
  validUntil?: string | Date | null
): string | null {
  if (!validFrom && !validUntil) return null;

  const start = validFrom ? toDate(validFrom) : null;
  const end = validUntil ? toDate(validUntil) : null;

  // Single date cases
  if (start && !end) return `From ${formatDatePart(start, true)}`;
  if (!start && end) return `Until ${formatDatePart(end, true)}`;
  if (!start || !end) return null;

  // Same day - just show single date
  if (isSameDay(start, end)) return formatDatePart(start, true);

  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameYear) {
    // Same year: "Jan 29 – Feb 4, 2024" or "Jan 8 – Jan 14, 2026"
    const startText = formatDatePart(start, false); // No year on start
    const endText = formatDatePart(end, true);      // Year on end
    return `${startText} – ${endText}`;
  } else {
    // Different years: "Dec 28, 2024 – Jan 3, 2025"
    const startText = formatDatePart(start, true);
    const endText = formatDatePart(end, true);
    return `${startText} – ${endText}`;
  }
}

/**
 * Format validity dates for a deal with fallback to raw text.
 * Prefers structured dates, falls back to raw_text if no dates available.
 */
export function formatValidityWithFallback(
  validFrom?: string | Date | null,
  validUntil?: string | Date | null,
  rawText?: string | null
): string {
  const formatted = formatEligibilityRange(validFrom, validUntil);
  if (formatted) return formatted;
  
  // Fallback to raw text if provided (do not attempt to parse/format)
  if (rawText && rawText.trim()) return rawText;
  
  return "Valid dates unknown";
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Convert string or Date to Date object, returns null if invalid.
 * CR412: For date-only strings ("YYYY-MM-DD"), parse as local to prevent TZ shift.
 */
function toDate(d: string | Date): Date | null {
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    // Date-only string → parse as local calendar date (no TZ shift)
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  }
  const date = d instanceof Date ? d : new Date(d);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a single date part.
 * @param withYear - If true, includes year (e.g., "Jan 29, 2024")
 */
function formatDatePart(d: Date, withYear: boolean): string {
  const options: Intl.DateTimeFormatOptions = withYear
    ? { month: "short", day: "numeric", year: "numeric" }
    : { month: "short", day: "numeric" };
  
  // Use en-US for consistent "Jan 29, 2024" format
  return new Intl.DateTimeFormat("en-US", options).format(d);
}

/**
 * Check if two dates are the same calendar day.
 */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
